/**
 * Pure helper functions for organizing invoice orders and line items.
 * Keeping these independent of Vue/Pinia state keeps our stores focused strictly on API data fetching.
 */
export function groupOrders(orders) {
    const projectMap = new Map();
    const projectUnassignedMap = new Map();
    const standaloneRolloutMap = new Map();
    const unassignedOrders = [];
    const displayProjectNames = new Map();
    orders.forEach((order) => {
        const hasProject = Boolean(order.projectName?.trim());
        const hasRollout = Boolean(order.specialPricingName?.trim());
        let projKey = '';
        if (hasProject) {
            const rawProj = order.projectName.trim();
            projKey = rawProj.toLowerCase();
            if (!displayProjectNames.has(projKey)) {
                displayProjectNames.set(projKey, rawProj);
            }
        }
        if (hasProject && hasRollout) {
            const rollout = order.specialPricingName;
            if (!projectMap.has(projKey))
                projectMap.set(projKey, new Map());
            const rollouts = projectMap.get(projKey);
            if (!rollouts.has(rollout))
                rollouts.set(rollout, []);
            rollouts.get(rollout).push(order);
        }
        else if (hasProject) {
            if (!projectUnassignedMap.has(projKey))
                projectUnassignedMap.set(projKey, []);
            projectUnassignedMap.get(projKey).push(order);
        }
        else if (hasRollout) {
            const rollout = order.specialPricingName;
            if (!standaloneRolloutMap.has(rollout))
                standaloneRolloutMap.set(rollout, []);
            standaloneRolloutMap.get(rollout).push(order);
        }
        else {
            unassignedOrders.push(order);
        }
    });
    const projects = [];
    const allProjectKeys = new Set([...projectMap.keys(), ...projectUnassignedMap.keys()]);
    allProjectKeys.forEach((projKey) => {
        const rolloutMap = projectMap.get(projKey);
        const rollouts = [];
        if (rolloutMap) {
            rolloutMap.forEach((orders, rolloutName) => {
                rollouts.push({ name: rolloutName, orders });
            });
        }
        projects.push({
            name: displayProjectNames.get(projKey) || projKey,
            rollouts,
            unassignedOrders: projectUnassignedMap.get(projKey) || [],
        });
    });
    const standaloneRollouts = [];
    standaloneRolloutMap.forEach((orders, rolloutName) => {
        standaloneRollouts.push({ name: rolloutName, orders });
    });
    return { projects, standaloneRollouts, unassignedOrders };
}
export function groupInvoiceLines(lines, orders = []) {
    const rawSections = [];
    const orderMap = new Map();
    orders.forEach((o) => orderMap.set(o.orderNum.trim(), o));
    let currentSection = { lines: [] };
    lines.forEach((line) => {
        const isHeaderCandidate = !line.itemCode && line.description && line.quantity === undefined;
        if (isHeaderCandidate) {
            const desc = line.description.trim();
            const extractedOrderNum = desc.split('/')[0].trim();
            const matchedOrder = orderMap.get(extractedOrderNum);
            if (matchedOrder) {
                if (currentSection.lines.length > 0 || currentSection.orderNum) {
                    rawSections.push(currentSection);
                }
                currentSection = {
                    orderNum: matchedOrder.orderNum,
                    orderID: matchedOrder.id,
                    projectName: matchedOrder.projectName || '',
                    specialPricingName: matchedOrder.specialPricingName || '',
                    lines: [],
                };
                return;
            }
        }
        if (line.itemCode || line.description) {
            currentSection.lines.push(line);
        }
    });
    if (currentSection.lines.length > 0) {
        rawSections.push(currentSection);
    }
    return rawSections.sort((a, b) => {
        const projA = (a.projectName || '').trim().toLowerCase();
        const projB = (b.projectName || '').trim().toLowerCase();
        if (!projA && projB)
            return 1;
        if (projA && !projB)
            return -1;
        if (projA !== projB)
            return projA.localeCompare(projB);
        const rolloutA = (a.specialPricingName || '').trim().toLowerCase();
        const rolloutB = (b.specialPricingName || '').trim().toLowerCase();
        if (rolloutA !== rolloutB)
            return rolloutA.localeCompare(rolloutB);
        return (a.orderNum || '').localeCompare(b.orderNum || '', undefined, { numeric: true });
    });
}
export function getInvoiceOrderTags(invoiceOrders) {
    const projectNames = new Set();
    const specialPricingNames = new Set();
    for (const order of invoiceOrders ?? []) {
        if (order.projectName)
            projectNames.add(order.projectName);
        if (order.specialPricingName)
            specialPricingNames.add(order.specialPricingName);
    }
    return {
        projectNames: Array.from(projectNames),
        specialPricingNames: Array.from(specialPricingNames),
    };
}
